import { type AdvisorConversationKind, getAdvisorSupportingText, type AdvisorFeedback } from '@ppwr/convex/players/types'
import type { AdvisorHandObservation, AdvisorStreet } from './actionAdvisor.outcome'

function sameConcept(left: AdvisorFeedback, right: AdvisorFeedback): boolean {
  return left.decision.concept === right.decision.concept
}

/** Word-level overlap between two supporting lines (Jaccard on lower-cased
 * tokens). Used only to rank variants of the same argument against each
 * other, never as an absolute similarity gate — cheap and good enough to
 * tell "the advice's own words" from "a fresh phrasing of the same reason". */
function supportingTextOverlap(left: string, right: string): number {
  const tokens = (line: string) => new Set(line.toLowerCase().split(/[^a-z0-9%]+/).filter(Boolean))
  const leftTokens = tokens(left)
  const rightTokens = tokens(right)
  if (leftTokens.size === 0 && rightTokens.size === 0) return 1
  let shared = 0
  leftTokens.forEach((token) => {
    if (rightTokens.has(token)) shared += 1
  })
  return shared / (leftTokens.size + rightTokens.size - shared)
}

/**
 * The first grade after deviating from just-shown advice re-argues the very
 * spot the advice bubble argued seconds earlier, and the backend builds the
 * difference line and its alternative from the same reason pools — so the
 * primary variant often echoes the advice's own wording back at the player
 * ("... because you are facing a raise to 300 chips ..." right after advice
 * that said the same). Pick whichever variant overlaps the advice's
 * supporting text LESS, so the correction lands in fresh words while still
 * carrying its own reason. The advice line itself is never a candidate — it
 * is a reason hint, not a difference template.
 */
function pickFresherDifferenceLine(current: AdvisorFeedback, advice: AdvisorFeedback): AdvisorFeedback {
  const primary = current.differenceReason
  const alternative = current.differenceReasonAlternative
  if (!primary || !alternative) return current

  const adviceText = getAdvisorSupportingText(advice)
  if (supportingTextOverlap(alternative, adviceText) < supportingTextOverlap(primary, adviceText)) {
    return { ...current, differenceReason: alternative, differenceReasonAlternative: primary }
  }
  return current
}

/**
 * Consume the turn-start advice on the first grade after it:
 * - Followed the recommendation → the grade is marked 'confirmed'. The UI
 *   shows NO follow-up bubble (the advice already made the argument) — just a
 *   thumbs-up on the avatar (see `thumbsUpRequestId` in the store). The
 *   observation is still recorded so end-of-hand recaps stay accurate.
 * - Deviated → the normal deviation copy says everything worth saying; pass
 *   it through with the advice marked as its prior context, preferring the
 *   difference variant least similar to the advice the player just read.
 */
function reconcileAfterAdvice(current: AdvisorFeedback, advice: AdvisorFeedback): AdvisorFeedback {
  if (current.grade === 'aligned') {
    return {
      ...current,
      conversation: { kind: 'confirmed', priorStreet: advice.decision.street }
    }
  }

  return {
    ...pickFresherDifferenceLine(current, advice),
    conversation: { kind: 'new', priorStreet: advice.decision.street }
  }
}

function classifyConversation(
  current: AdvisorFeedback,
  observations: readonly AdvisorHandObservation[]
): { kind: AdvisorConversationKind; prior?: AdvisorFeedback } {
  const previous = observations.at(-1)?.feedback
  const related = [...observations].reverse().find(({ feedback }) => sameConcept(feedback, current))?.feedback
  const currentNeedsAdjustment = current.grade !== 'aligned'
  const priorNeededAdjustment = related?.grade !== 'aligned'

  if (
    related &&
    currentNeedsAdjustment &&
    priorNeededAdjustment &&
    current.decision.deviation === related.decision.deviation &&
    current.decision.recommendedAction === related.decision.recommendedAction
  ) {
    return { kind: 'repeated', prior: related }
  }

  const priorPlan = previous
  if (
    priorPlan &&
    current.decision.street !== priorPlan.decision.street &&
    current.decision.recommendedAction !== priorPlan.decision.recommendedAction
  ) {
    return { kind: 'reversed', prior: priorPlan }
  }

  if (related && !currentNeedsAdjustment && priorNeededAdjustment) {
    return { kind: 'corrected', prior: related }
  }

  if (related && current.decision.street !== related.decision.street) {
    return { kind: 'continued', prior: related }
  }

  return { kind: 'new' }
}

type TransitionContext = {
  priorStreet: AdvisorStreet
  priorConcept: string
  priorDeviation: string
  priorAction: string
  priorRecommendedAction: string
  currentStreet: AdvisorStreet
  currentConcept: string
  currentAction: string
  currentRecommendedAction: string
  /** The street where the situation changed (the street between prior and current). */
  changedOn: AdvisorStreet
}

export function reconcileAdvisorFeedback(
  current: AdvisorFeedback,
  observations: readonly AdvisorHandObservation[],
  pendingAdvice?: AdvisorFeedback
): AdvisorFeedback {
  // The first grade after turn-start advice is that advice's follow-up: a
  // brief confirmation when the player followed it, deviation copy otherwise.
  // One message per event — no third bubble.
  if (pendingAdvice) {
    return reconcileAfterAdvice(current, pendingAdvice)
  }

  const { kind, prior } = classifyConversation(current, observations)
  if (kind === 'new' || !prior) {
    return { ...current, conversation: { kind: 'new' } }
  }

  const transition = buildTransitionContext(kind, prior, current)

  return {
    ...current,
    conversation: { kind, priorStreet: prior.decision.street, transition }
  }
}

function buildTransitionContext(
  kind: AdvisorConversationKind,
  prior: AdvisorFeedback,
  current: AdvisorFeedback
): TransitionContext | undefined {
  if (kind === 'new' || kind === 'continued') return undefined

  return {
    priorStreet: prior.decision.street,
    priorConcept: prior.decision.concept,
    priorDeviation: prior.decision.deviation,
    priorAction: prior.decision.actualAction,
    priorRecommendedAction: prior.decision.recommendedAction,
    currentStreet: current.decision.street,
    currentConcept: current.decision.concept,
    currentAction: current.decision.actualAction,
    currentRecommendedAction: current.decision.recommendedAction,
    changedOn: current.decision.street
  }
}
